import { useMsal } from "@azure/msal-react";
import React, { Dispatch, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "semantic-ui-react";
import { getTenantLogoBySubdomain, getTenantMetadataBySubdomain } from "../api/GlobalApi";
import { getMockTenantLogoBySubdomain, getMockTenantMetadataBySubdomain } from "../api/mock/MockGlobalApi";
import { convertHexToRgb, createFile, getDropdownOptions, handleErrorResponse, handleFormChange } from "../Helpers";
import "../styles/ui.scss";
import "../styles/welcome.scss";
import { IErrorResponse } from "./interfaces/Global";
import BCFormInput from "./ui/BCFormInput";
import VentureWelcome from "../assets/venture-welcome.jpg";
import ClsLogo from "../assets/cls-logo.svg";
import { css } from "@emotion/css";
import { getMockProfile } from "../api/mock/MockBrokerApi";
import { getProfile } from "../api/BrokerApi";
import { getEnvironmentDomain, getSubdomainFromUrl, handleCreateAuthCookies, logout } from "../helpers/GlobalHelper";
import { MsalConstants } from "./constants/GlobalConstants";
import { FeatureFlags } from "./constants/LocalServerConstants";
import { getIsWhiteLabeled } from "../helpers/PermissionHelper";

interface IBrokerage {
  subdomain: string;
  name: string;
  useWhitelabelPreferences: boolean;
}

interface IBCWelcomeProps {
  isBrokerages: boolean,
  isMessage: boolean,
  setIsMessage: Dispatch<boolean>;
  errorResponse: IErrorResponse | undefined;
  setErrorResponse: Dispatch<IErrorResponse | undefined>;
  proxyUser: any;
  setCookie: any;
  removeCookie: any;
}

const BCBrokerage = ({...props} : IBCWelcomeProps) => {
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isMultiBrokerage, setIsMultiBrokerage] = useState(false as boolean);
  const [brokerages, setBrokerages] = useState([] as IBrokerage[]);
  const [brokerageObj, setBrokerageObj] = useState({} as any);
  const gradientColor1Default = "rgba(0,0,0,1)";
  const gradientColor2Default = "rgba(255,255,255,0.1)";
  const brokerageColor1Default = "rgba(0,0,0,1)";
  const brokerageColor2Default = "rgba(0,0,0,0.1)";
  const [brokerageColor1, setBrokerageColor1] = useState(brokerageColor1Default as string);
  const [brokerageColor2, setBrokerageColor2] = useState(brokerageColor2Default as string);
  const [brokerageLogo, setBrokerageLogo] = useState(undefined as string | undefined);
  const [brokerageLogoHeight, setBrokerageLogoHeight] = useState(0 as number);
  const [brokerageLogoWidth, setBrokerageLogoWidth] = useState(0 as number);
  const { instance, accounts } = useMsal();
  const history = useHistory();
  
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleRouteToHome = useCallback(() => {
    history.push("/");
  }, [history]);

  const handleBackgroundAnimation = (gradientColor1 = gradientColor1Default as string, gradientColor2 = gradientColor2Default as string) => {
    const random = (min, max) => {
      return (Math.random() * (min - max + 1) ) << 0;
    };

    const requestAnimFrame = (() => {
      return window.requestAnimationFrame ||
        function(callback) {
          window.setTimeout(callback, 1000 / 60);
        };
    })();

    const animate = () => {
      requestAnimFrame(animate);
      draw();
    };

    const draw = () => {
      const time = new Date().getTime() * 0.002;
      const color1 = gradientColor1 ? gradientColor1 : "rgba(0,0,0,1)";
      const color2 = gradientColor2 ? gradientColor2 : "rgba(255,255,255,0.1)";
      const canvas = document.getElementById("hero-canvas") as HTMLCanvasElement;
      
      if (canvas) {
        const ctx = canvas.getContext("2d");
        const background = new window.Image();
        background.src = VentureWelcome;

        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.save();
          const randomX = random(.2, .9);
          const randomY = random(.1, .2);
          const rectX = Math.cos(time * 1) * 1.5 + randomX;
          const rectY = Math.sin(time * 1) * 1.5 + randomY;
          const rectX2 = Math.cos(time * .7) * 3 + randomX;
          const rectY2 = Math.sin(time * .7) * 3 + randomY;
          const rectX3 = Math.cos(time * 1.4) * 4 + randomX;
          const rectY3 = Math.sin(time * 1.4) * 4 + randomY;
    
          const triangle_gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          triangle_gradient.addColorStop(0, color1);
          triangle_gradient.addColorStop(1, color2);

          if (!props.isBrokerages) {
            ctx.drawImage(background, canvas.width / 8, 0, canvas.width / 2.665, canvas.height);
          }
          
          ctx.beginPath();
          ctx.moveTo(rectX - 50, rectY + (canvas.height - 600));
          ctx.lineTo(rectX + 470, rectY + (canvas.height - 30));
          ctx.lineTo(rectX - 50, rectY + (canvas.height + 150));
          ctx.fillStyle = triangle_gradient;
          ctx.fill();

          ctx.beginPath();
          ctx.moveTo(rectX2 + 120, rectY2 + (canvas.height - 400));
          ctx.lineTo(rectX2 + 460, rectY2 + (canvas.height - 80));
          ctx.lineTo(rectX2 + 26, rectY2 + (canvas.height - 20));
          ctx.fillStyle = triangle_gradient;
          ctx.fill();

          ctx.beginPath();
          ctx.moveTo(rectX3 + 50, rectY3 + (canvas.height - 350));
          ctx.lineTo(rectX3 + 580, rectY + (canvas.height + 50));
          ctx.lineTo(rectX3 + 125, rectY3 + (canvas.height + 30));
          ctx.fillStyle = triangle_gradient;
          ctx.fill();

          ctx.restore();
        }
      }
    };

    animate();
  };

  const handleBrokerageFormChange = (value, field) => {
    handleFormChange(value, field, brokerageObj, setBrokerageObj);
  };

  const handleSelectBrokerage = () => {
    if (brokerageObj.brokerageName) {
      const foundBrokerage = brokerages.find(x => x.name === brokerageObj.brokerageName);
      if (foundBrokerage) {
        if (process.env.REACT_APP_ENV === "LOCAL" || (foundBrokerage.useWhitelabelPreferences && foundBrokerage.subdomain === null)) {
          localStorage.setItem("brokerage", JSON.stringify(foundBrokerage));
          handleRouteToHome();
        }
        else if (foundBrokerage.useWhitelabelPreferences && foundBrokerage.subdomain) {
          handleCreateAuthCookies(props.setCookie);
          window.location.href = `https://${foundBrokerage.subdomain}${getEnvironmentDomain()}/subdomain`;
        }
        else if (foundBrokerage.useWhitelabelPreferences === false) {
          handleCreateAuthCookies(props.setCookie);
          window.location.href = `https://builderscapital${getEnvironmentDomain()}/subdomain`;
        }
      }
    }
  };

  const handleLogin = () => {
    localStorage.setItem("session_login", "YES");
    const subdomain = getSubdomainFromUrl();
    if (getIsWhiteLabeled() && process.env.REACT_APP_ENV !== "LOCAL" && 
    subdomain && subdomain !== MsalConstants.CentralRedirectSubdomain) {
      const sylDomain = getEnvironmentDomain();
      props.setCookie("msal_subdomain", subdomain, { domain: sylDomain });
      window.location.href = `https://${MsalConstants.CentralRedirectSubdomain}${getEnvironmentDomain()}/redirect`;
    }
    else {
      instance.loginRedirect(); 
    }
  };

  const handleBrokerageLogoLoad = () => {
    if (brokerageLogo) {
      const image = new Image();
      image.src = brokerageLogo as any;
      const currentRatio = image.width / image.height;
      if (currentRatio < 2.6) {
        setBrokerageLogoHeight(100);
        setBrokerageLogoWidth(100 * currentRatio);
      }
      else {
        setBrokerageLogoWidth(260);
        setBrokerageLogoHeight(260 / currentRatio);
      }
    }
  };

  useEffect(() => {
    const calculateBackgroundGradient = (r2r, r2g, r2b, diff) => {
      const r1r = (r2r - diff > 0) ? r2r - diff : 0;
      const r1g = (r2g - diff > 0) ? r2g - diff : 0;
      const r1b = (r2b - diff > 0) ? r2b - diff : 0;
      return {
        backgroundColor1: `rgba(${r1r},${r1g},${r1b},1)`,
        backgroundColor2: `rgba(${r2r},${r2g},${r2b},0.1)`
      };
    };

    const getProfileBrokeragesAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockProfile() : await getProfile();
        if (response && response.ok) {
          const profile = await response.json() as any;
          if (profile.brokerages) {
            const brokerages = profile.brokerages;
            const sylSubdomain = getSubdomainFromUrl();
            if (sylSubdomain && sylSubdomain !== MsalConstants.CentralRedirectSubdomain) {
              const foundBrokerage = brokerages.find(x => (x.subdomain && x.subdomain.toLocaleLowerCase() === sylSubdomain.toLocaleLowerCase()));
              if (foundBrokerage) {
                localStorage.setItem("brokerage", JSON.stringify(foundBrokerage));
                handleRouteToHome();
              }
              else {
                // TODO: Handle brokerage subdomain mismatch phase 3
                logout(instance, props.removeCookie);
              }
            }
            else {
              if (brokerages && brokerages.length > 1) {
                handleBackgroundAnimation();
                setIsMultiBrokerage(true);
                setBrokerages(brokerages);
              }
              else if (brokerages && brokerages.length === 1) {
                handleRouteToHome();
              }
              else {
                handleError();
              }
    
              if (localStorage.getItem("brokerage")) {
                const brokerageObjString = localStorage.getItem("brokerage") as any;
                const brokerageObj = JSON.parse(brokerageObjString);
                setBrokerageObj({ brokerageName: brokerageObj.name });
              }
    
              setIsLoading(false);
              return brokerages;
            }
          }
        }
        else {
          await handleErrorResponse(response, props.setIsMessage, props.setErrorResponse);
        }
      }
      catch (error: any) {
        handleError();
      }

      setIsLoading(false);
    };

    const getTenantMetadataBySubdomainAsync = async (subdomain) => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockTenantMetadataBySubdomain(subdomain) : await getTenantMetadataBySubdomain(subdomain);
        if (response && response.ok) {
          const tenantMetadata = await response.json();
          let backgroundColor1 = gradientColor1Default;
          let backgroundColor2 = gradientColor2Default;
          let brokerageColor1 = brokerageColor1Default;
          let brokerageColor2 = brokerageColor2Default;
          if (tenantMetadata && tenantMetadata.themeColor) {
            const rgbObj = convertHexToRgb(tenantMetadata.themeColor);
            const gradientObj = calculateBackgroundGradient(rgbObj.r, rgbObj.g, rgbObj.b, 10);
            backgroundColor1 = gradientObj.backgroundColor1;
            backgroundColor2 = gradientObj.backgroundColor2;
            brokerageColor1 = `rgba(${rgbObj.r},${rgbObj.g},${rgbObj.b},1)`;
            brokerageColor2 = `rgba(${rgbObj.r},${rgbObj.g},${rgbObj.b},0.1)`;
            setBrokerageColor1(brokerageColor1);
            setBrokerageColor2(brokerageColor2);
          }

          handleBackgroundAnimation(backgroundColor1, backgroundColor2);
          return subdomain;
        }
        else {
          await handleErrorResponse(response, props.setIsMessage, props.setErrorResponse);
          handleBackgroundAnimation();
        }
      }
      catch (error) {
        handleBackgroundAnimation();
      }
      
      setIsLoading(false);
    };

    const getTenantLogoAsync = async (subdomain) => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockTenantLogoBySubdomain(subdomain) : await getTenantLogoBySubdomain(subdomain) as any;
        if (response && response.ok) {
          const data = await response.blob();
          const tenantLogo = await createFile(data, "profile-avatar", data.type);
          setBrokerageLogo(URL.createObjectURL(tenantLogo));
        }
        else {
          await handleErrorResponse(response, props.setIsMessage, props.setErrorResponse);
          setBrokerageLogo(undefined);
        }
      }
      catch (error: any) {
        setBrokerageLogo(undefined);
      }

      setIsLoading(false);
    };

    const handleSylSubdomain = async (sylSubdomain) => {
      const domain = await getTenantMetadataBySubdomainAsync(sylSubdomain);
      if (!domain) {
        window.location.href = `https://${MsalConstants.CentralRedirectSubdomain}${getEnvironmentDomain()}/welcome`;
      }
    };

    if (isFirstLoad) {
      if (props.isBrokerages) {
        if (getIsWhiteLabeled()) {
          getProfileBrokeragesAsync();
        }
        else {
          handleRouteToHome();
        }
      }
      else {
        if (localStorage.getItem("session_login") === "YES") {
          localStorage.clear();
          handleRouteToHome();
        }

        if (accounts && accounts.length > 0) {
          handleRouteToHome();
        }

        if (getIsWhiteLabeled()) {
          const sylSubdomain = getSubdomainFromUrl();
          if (sylSubdomain && sylSubdomain !== MsalConstants.CentralRedirectSubdomain) {
            handleSylSubdomain(sylSubdomain);
            getTenantLogoAsync(sylSubdomain);
          }
          else {
            handleBackgroundAnimation();
            setIsLoading(false);
          }
        }
        else {
          handleBackgroundAnimation("rgba(94, 166, 18, 1)", "rgba(94, 166, 18, 0.1)");
          setIsLoading(false);
        }
      }
    }
  }, [
    isFirstLoad, 
    props.setIsMessage, 
    props.setErrorResponse, 
    handleError, 
    props.isBrokerages, 
    convertHexToRgb,
    gradientColor1Default,
    gradientColor2Default,
    handleRouteToHome,
    brokerageColor1Default,
    brokerageColor2Default
  ]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  return (
    <React.Fragment>
      {!isLoading && <div className="container hero">
        <div className="inner">
          {isMultiBrokerage ? <div className="brokerages-container">
            <div className="brokerages-text">
              <span>Welcome back! Please select from the list below the company you are working with.</span>
            </div>
            <BCFormInput 
              inputDropdownClass="brokerages-dropdown"
              type="dropdown"
              obj={brokerageObj}
              field="brokerageName"
              dropdownOptions={getDropdownOptions(Array.from(brokerages, x => x.name))}
              handleFormChange={(value, field) => handleBrokerageFormChange(value, field)}
            />
            <Button className="brokerages-button" color="black" onClick={handleSelectBrokerage}>Login</Button>
          </div> : <div className="welcome-container">
            <div className="welcome-right">
              <div className="welcome-content">
                <div className="welcome-login-bar">
                  <Button className="welcome-bar-button" color="black" onClick={handleLogin}>Login</Button>
                </div>
                {(getIsWhiteLabeled() && brokerageLogo) ? <img 
                  onLoad={handleBrokerageLogoLoad}
                  src={brokerageLogo} 
                  alt="Broker Logo"
                  height={`${brokerageLogoHeight}px`} 
                  width={`${brokerageLogoWidth}px`} 
                /> : !getIsWhiteLabeled() ? <img 
                  src={ClsLogo} 
                  alt="ClsLogo" 
                  width="260px" 
                  height="100px" 
                /> : <div className="welcome-logo-spacer" />}
                <div className="welcome-title">
                  <div>WELCOME</div>
                  <div>TO YOUR PORTAL</div>
                  <div>EXPERIENCE</div>
                </div>
                <div className="welcome-subtext">We are committed to your success.</div>
                <div className={!getIsWhiteLabeled() ? "welcome-gradient" : css`
                  background-image: linear-gradient(to right, ${brokerageColor1}, ${brokerageColor2});
                  height: 20px;
                  width: 50%;
                  margin-top: 40px;
                `} />
              </div>
            </div>
          </div>}
        </div>
        <div className="overlay" />
        <div className="background"> 
          <canvas id="hero-canvas" width="1920" height="1080"></canvas>
        </div>
      </div>}
    </React.Fragment>
  );
};

export default BCBrokerage;

